import { useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import { 
  Refund,
  QueryFindRefundsArgs 
} from "../../../../schemaTypes";

export const findRefundsQuery = gql`
  query FindRefundsQuery(
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $searchText: String
  ) {
    findRefunds(
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      searchText: $searchText
    ) {
      refund {
        id
        amount
        reasonForRefund
        description
        customerName
        transactionId
        createdAt
        reference
        status
        currency
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;
   
export const newRefundSubscription = gql`
  subscription NewRefundSubscription {
    newRefund {
      id
      amount
      reasonForRefund
      description
      customerName
      transactionId
      createdAt
      reference
      status
      currency
    }
  }
`;

export interface WithFindRefunds {
  refund: Refund[];
  loading: boolean;
  loadMore: () => void;
  hasMoreRefunds: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface Props {
  variables: QueryFindRefundsArgs;
  children: (data: WithFindRefunds) => React.ReactElement | null;
}

export const FindRefundsController: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    any,
    QueryFindRefundsArgs
  >(findRefundsQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const refundsData = data?.findRefunds || {
    refund: [],
    total: 0,
    page: 1,
    pageSize: variables.pageSize || 10,
    totalPages: 1,
  };

  const refund = refundsData.refund;
  const page = refundsData.page;
  const pageSize = refundsData.pageSize;
  const total = refundsData.total;
  const totalPages = refundsData.totalPages;
  const hasMoreRefunds = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreRefunds) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findRefunds) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findRefunds: {
            ...fetchMoreResult.findRefunds,
            refund: [
              ...(prev?.findRefunds?.refund || []),
              ...(fetchMoreResult.findRefunds.refund || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreRefunds]);

  return children({
    refund,
    loading,
    loadMore,
    hasMoreRefunds,
    page,
    pageSize,
    total,
    totalPages,
  });
};